import { Button, Select, Space, Avatar, List, Tag, Tooltip } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { Typography } from "antd"
import { useState } from "react"

import { CrewMember } from "./scheduleApi"
import { getInitials } from "./scheduleModel"

const { Text } = Typography

type Props = {
  crew: CrewMember[]
  selectedCrew: CrewMember[]
  crewLeadId: number | null
  handleCrewChange: (value: number[]) => void
  handleApply: () => void
  hasChanges: boolean
  setCrewLead: (memberId: number) => void
  removeCrewMember: (memberId: number) => void
}

const SelectCrewDropdown = ({ crew, selectedCrew, handleCrewChange, handleApply, hasChanges, crewLeadId, setCrewLead, removeCrewMember }: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  return <div style={{
    width: '550px',
    padding: '12px',
    backgroundColor: 'white',
    border: '1px solid #d9d9d9',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
  }}>
    <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
      <Select
        mode="multiple"
        maxTagCount={2}
        placeholder="Search Crew Members"
        value={selectedCrew.map(c => c.id)}
        onChange={handleCrewChange}
        style={{ flex: 1 }}
        showSearch
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        tagRender={(props) => {
          const member = crew?.find(c => c.id === props.value)
          if (!member) {
            return <span>{props.label}</span>
          }
          return (
            <Tag
              closable={props.closable}
              onClose={props.onClose}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '4px', padding: '2px 8px 2px 4px' }}
            >
              <Avatar
                src={member.hasStockAvatar ? undefined : member.avatar}
                size={20}
                style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff', fontSize: '10px' } : undefined}
              >
                {getInitials(member.name)}
              </Avatar>
              <span>{member.name}</span>
            </Tag>
          )
        }}
        popupRender={(menu) => (
          <>
            {menu}
            <div style={{ padding: '8px', borderTop: '1px solid #d9d9d9', textAlign: 'center' }}>
              <Button
                type="primary"
                size="small"
                onClick={() => setDropdownOpen(false)}
                style={{ width: '100%' }}
              >
                Done
              </Button>
            </div>
          </>
        )}
        filterOption={(input, option) => {
          const member = crew?.find(c => c.id === option?.value)
          return member?.name.toLowerCase().includes(input.toLowerCase()) || false
        }}
      >
        {crew?.map(member => (
          <Select.Option key={member.id} value={member.id}>
            <Space>
              <Avatar
              src={member.hasStockAvatar ? undefined : member.avatar}
              size="small"
              style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
            >
              {getInitials(member.name)}
            </Avatar>
              <span>{member.name}</span>
            </Space>
          </Select.Option>
        ))}
      </Select>
      <Button 
        type="primary" 
        size="small"
        onClick={handleApply}
        disabled={!hasChanges}
      >
        Save
      </Button>
    </div>
    
    {selectedCrew.length > 0 ? (
      <List
        size="small"
        dataSource={selectedCrew}
        renderItem={member => (
          <List.Item
            actions={[
              !member.userEmail ? (
                <Tooltip title="Crew member needs an email address to be a crew lead">
                  <Button
                    type="link"
                    size="small"
                    disabled
                    style={{
                      padding: 0,
                      cursor: 'not-allowed'
                    }}
                  >
                    Set as Crew Lead
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setCrewLead(member.id)}
                  style={{
                    color: crewLeadId === member.id ? '#d4380d' : '#1890ff',
                    padding: 0
                  }}
                >
                  {crewLeadId !== member.id ? 'Set as Crew Lead' : 'Remove Crew Lead'}
                </Button>
              ),
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => removeCrewMember(member.id)}
                style={{ color: '#999' }}
              />
            ]}
          >
            <List.Item.Meta
              avatar={
                <Avatar
                  src={member.hasStockAvatar ? undefined : member.avatar}
                  size="small"
                  style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
                >
                  {getInitials(member.name)}
                </Avatar>
              }
              title={
                <Space>
                  {member.name}
                  {crewLeadId === member.id && (
                    <Tag color="gold">Crew Lead</Tag>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    ) : (
      <div style={{
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        padding: '40px 20px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        minHeight: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Text type="secondary">No Assigned Crew</Text>
      </div>
    )}
  </div>
}

export default SelectCrewDropdown